import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import usageTracker from '../services/usageTracker';

const { FiX, FiBarChart, FiTrendingUp, FiTarget, FiCalendar, FiStar, FiActivity } = FiIcons;

const AnalyticsModal = ({ isOpen, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadAnalytics();
    }
  }, [isOpen]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const usage = await usageTracker.getCurrentUserUsage();
      const remaining = await usageTracker.getRemainingFreeCalculations();
      
      // Calculate additional metrics
      const successRate = usage.totalCalculations > 0 
        ? (usage.successfulCalculations / usage.totalCalculations) * 100 
        : 0;
      
      const daysUsed = usage.firstUse 
        ? Math.max(1, Math.ceil((Date.now() - usage.firstUse) / (1000 * 60 * 60 * 24)))
        : 1;
      
      const avgCalculationsPerDay = usage.totalCalculations / daysUsed;

      setAnalytics({
        ...usage,
        remaining,
        successRate,
        daysUsed,
        avgCalculationsPerDay
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiBarChart} className="text-indigo-600" />
            Your Analytics
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
              <span className="ml-3 text-gray-600">Loading analytics...</span>
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Account Status */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Account Status</h3>
                    <p className="text-gray-600">
                      {analytics.hasPaid ? 'Pro User' : 'Free User'}
                    </p>
                  </div>
                  <div className="text-right">
                    {analytics.hasPaid ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <SafeIcon icon={FiStar} className="text-2xl" />
                        <span className="font-semibold">Unlimited</span>
                      </div>
                    ) : (
                      <div className="text-orange-600">
                        <div className="text-2xl font-bold">{analytics.remaining}</div>
                        <div className="text-sm">calculations left</div>
                      </div>
                    )}
                  </div>
                </div>
                {analytics.hasPaid && analytics.paymentDate && (
                  <div className="mt-3 text-sm text-gray-500">
                    Upgraded on {new Date(analytics.paymentDate).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {analytics.totalCalculations}
                      </div>
                      <div className="text-sm text-blue-700">Total Calculations</div>
                    </div>
                    <SafeIcon icon={FiActivity} className="text-2xl text-blue-500" />
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {analytics.successfulCalculations}
                      </div>
                      <div className="text-sm text-green-700">Successful Calculations</div>
                    </div>
                    <SafeIcon icon={FiTarget} className="text-2xl text-green-500" />
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {Math.round(analytics.successRate)}%
                      </div>
                      <div className="text-sm text-purple-700">Success Rate</div>
                    </div>
                    <SafeIcon icon={FiTrendingUp} className="text-2xl text-purple-500" />
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {analytics.daysUsed}
                      </div>
                      <div className="text-sm text-orange-700">Days Used</div>
                    </div>
                    <SafeIcon icon={FiCalendar} className="text-2xl text-orange-500" />
                  </div>
                </div>
              </div>

              {/* Usage Insights */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Usage Insights</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average calculations per day:</span>
                    <span className="font-semibold">{analytics.avgCalculationsPerDay.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">First used:</span>
                    <span className="font-semibold">
                      {new Date(analytics.firstUse).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Last used:</span>
                    <span className="font-semibold">
                      {new Date(analytics.lastUse).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Performance Tips */}
              <div className="bg-indigo-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-indigo-800 mb-3">Performance Tips</h3>
                <div className="space-y-2 text-sm text-indigo-700">
                  {analytics.successRate < 70 && (
                    <div className="flex items-start gap-2">
                      <span className="text-orange-500">•</span>
                      <span>Try increasing your macro thresholds to improve success rate</span>
                    </div>
                  )}
                  {analytics.successRate >= 70 && analytics.successRate < 90 && (
                    <div className="flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      <span>Good success rate! Try tightening thresholds for more precise results</span>
                    </div>
                  )}
                  {analytics.successRate >= 90 && (
                    <div className="flex items-start gap-2">
                      <span className="text-green-500">•</span>
                      <span>Excellent success rate! You're using the calculator effectively</span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="text-indigo-500">•</span>
                    <span>Include foods from different macro categories for better optimization</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-indigo-500">•</span>
                    <span>Use wider serving ranges when you're flexible with portions</span>
                  </div>
                </div>
              </div>

              {/* Upgrade Prompt for Free Users */}
              {!analytics.hasPaid && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
                  <div className="flex items-center gap-3 mb-3">
                    <SafeIcon icon={FiStar} className="text-2xl text-yellow-600" />
                    <h3 className="text-lg font-semibold text-yellow-800">Upgrade to Pro</h3>
                  </div>
                  <p className="text-yellow-700 text-sm mb-4">
                    Get unlimited calculations, access to 500,000+ foods, and advanced features.
                  </p>
                  <div className="text-2xl font-bold text-yellow-800">
                    $9.99 <span className="text-sm font-normal">one-time payment</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No analytics data available
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AnalyticsModal;